import type { Chart } from "../chart/Chart";
import type { InteractionDelegate, PointerCtx } from "../chart/interaction/InputController";
import { DrawingModel, type DrawStyle, defaultStyle } from "./DrawingModel";
import { TOOL_DEFS, reviveDrawing, type ToolDef } from "./registry";
import { saveDrawings, loadDrawings } from "./DrawingStore";
import { pointNear } from "./geometry";

const HANDLE_TOL = 8;

export class DrawingLayer implements InteractionDelegate {
  private chart: Chart;
  private symbol: string;
  drawings: DrawingModel[] = [];
  selected: DrawingModel | null = null;
  activeTool = "cursor";
  magnet = false;

  currentStyle: DrawStyle = defaultStyle("#2962ff");

  private creating: DrawingModel | null = null;
  private creatingDef: ToolDef | null = null;
  private creatingForced = false; // started via Shift shortcut, not the toolbar
  private handleDrag: { kind: number } | null = null;
  private bodyDrag = false;
  private lastPx = 0;
  private lastPy = 0;
  private hovered: DrawingModel | null = null;
  // ephemeral shift-measure: not persisted, cleared on the next click
  private tempDrawing: DrawingModel | null = null;

  private history: string[] = [];
  private histIndex = 0;

  onChange: (() => void) | null = null;
  onActiveToolChange: (() => void) | null = null;

  constructor(chart: Chart, symbol: string) {
    this.chart = chart;
    this.symbol = symbol;
    this.drawings = loadDrawings(symbol);
    this.history = [this.snapshot()];
    this.histIndex = 0;
  }

  // ---------- tool / selection ----------
  setActiveTool(tool: string) {
    this.cancelCreate();
    this.activeTool = tool;
    if (tool !== "cursor") this.select(null);
    if (this.onActiveToolChange) this.onActiveToolChange();
    this.chart.container.style.cursor = "crosshair";
  }

  // Abort an in-progress (not-yet-finalized) drawing.
  cancelCreate() {
    if (!this.creating) return;
    const i = this.drawings.indexOf(this.creating);
    if (i >= 0) this.drawings.splice(i, 1);
    this.creating = null;
    this.creatingDef = null;
    this.creatingForced = false;
    this.select(null);
    this.chart.markDirty();
  }

  select(d: DrawingModel | null) {
    this.selected = d;
    this.chart.markOverlay();
    this.chart.markDirty();
  }

  setMagnet(on: boolean) {
    this.magnet = on;
  }

  hasSelection() {
    return this.selected !== null;
  }

  // ---------- history ----------
  private snapshot(): string {
    return JSON.stringify(this.drawings.map((d) => d.serialize()));
  }
  private restore(state: string) {
    const selId = this.selected?.id;
    this.drawings = (JSON.parse(state) as any[]).map(reviveDrawing).filter((d): d is DrawingModel => !!d);
    this.selected = selId ? this.drawings.find((d) => d.id === selId) ?? null : null;
    this.chart.markDirty();
    this.chart.markOverlay();
  }
  private commit() {
    const s = this.snapshot();
    this.history = this.history.slice(0, this.histIndex + 1);
    this.history.push(s);
    this.histIndex = this.history.length - 1;
    saveDrawings(this.symbol, this.drawings);
  }
  undo() {
    if (this.histIndex > 0) {
      this.histIndex--;
      this.restore(this.history[this.histIndex]);
      saveDrawings(this.symbol, this.drawings);
      this.notify();
    }
  }
  redo() {
    if (this.histIndex < this.history.length - 1) {
      this.histIndex++;
      this.restore(this.history[this.histIndex]);
      saveDrawings(this.symbol, this.drawings);
      this.notify();
    }
  }

  deleteSelected() {
    if (!this.selected) return;
    const i = this.drawings.indexOf(this.selected);
    if (i >= 0) this.drawings.splice(i, 1);
    this.select(null);
    this.commit();
    this.notify();
  }
  deleteAll() {
    if (!this.drawings.length) return;
    this.drawings = [];
    this.select(null);
    this.commit();
    this.notify();
  }

  setStyleOfSelected(patch: Partial<DrawStyle>) {
    if (!this.selected) return;
    Object.assign(this.selected.style, patch);
    Object.assign(this.currentStyle, patch);
    this.commit();
    this.chart.markDirty();
    this.notify();
  }

  editSelectedText() {
    if (!this.selected || this.selected.type !== "text") return;
    const t = window.prompt("Edit text", this.selected.style.text ?? "");
    if (t != null) {
      this.selected.style.text = t;
      this.commit();
      this.chart.markDirty();
    }
  }

  private notify() {
    if (this.onChange) this.onChange();
  }

  // ---------- snapping ----------
  private snapTime(ctx: PointerCtx): number {
    return this.chart.timeOfX(ctx.px);
  }
  private snapPrice(ctx: PointerCtx): number {
    if (!this.magnet || !this.chart.data) return ctx.price;
    const idx = ctx.index; // bar under the cursor (floor) — matches the crosshair
    if (idx < 0 || idx >= this.chart.effBarCount) return ctx.price;
    const o = this.chart.data.openAt(idx),
      h = this.chart.data.highAt(idx),
      l = this.chart.data.lowAt(idx),
      c = this.chart.data.closeAt(idx);
    let best = ctx.price,
      bestD = Infinity;
    for (const v of [o, h, l, c]) {
      if (!isFinite(v)) continue;
      const d = Math.abs(v - ctx.price);
      if (d < bestD) {
        bestD = d;
        best = v;
      }
    }
    return best;
  }

  // ---------- hit testing ----------
  private hitHandle(d: DrawingModel, px: number, py: number): number | null {
    for (const h of d.handles(this.chart)) {
      if (pointNear(px, py, h.x, h.y, HANDLE_TOL)) return h.kind;
    }
    return null;
  }
  private hitTopmost(px: number, py: number): DrawingModel | null {
    for (let i = this.drawings.length - 1; i >= 0; i--) {
      if (this.drawings[i].hitTest(px, py, this.chart)) return this.drawings[i];
    }
    return null;
  }

  // public: used by the context menu
  pickAt(px: number, py: number): DrawingModel | null {
    return this.hitTopmost(px, py);
  }
  selectAt(px: number, py: number): DrawingModel | null {
    const d = this.hitTopmost(px, py);
    this.select(d);
    this.notify();
    return d;
  }

  // ---------- InteractionDelegate ----------
  onDown(ctx: PointerCtx): boolean {
    const { px, py } = ctx;

    // second click while placing a drawing -> set final point & finalize
    if (this.creating && this.creatingDef) return this.advanceCreate(ctx);

    // a finished shift-measure is temporary: the next click clears it
    if (this.tempDrawing) {
      this.clearTemp();
      // if this click isn't starting a fresh measure, it was only a dismiss
      if (!(this.activeTool === "cursor" && ctx.event.shiftKey)) return true;
    }

    if (this.activeTool !== "cursor") return this.beginCreate(ctx, this.activeTool, false);

    // Shift + click in cursor mode = quick price measurement (two-click, temporary)
    if (ctx.event.shiftKey) return this.beginCreate(ctx, "pricerange", true);

    if (this.selected) {
      const k = this.hitHandle(this.selected, px, py);
      if (k != null) {
        this.handleDrag = { kind: k };
        return true;
      }
    }
    const hit = this.hitTopmost(px, py);
    if (hit) {
      this.select(hit);
      this.bodyDrag = true;
      this.lastPx = px;
      this.lastPy = py;
      this.notify();
      return true;
    }
    if (this.selected) {
      this.select(null);
      this.notify();
    }
    return false;
  }

  // First click: place the drawing's start point. For 1-point tools this also
  // finalizes; for 2-point tools we wait for a second click (see advanceCreate).
  private beginCreate(ctx: PointerCtx, toolId: string, forced: boolean): boolean {
    const def = TOOL_DEFS[toolId];
    if (!def) return false;
    const style: DrawStyle = { ...this.currentStyle };
    const d = def.make(style);
    const t = this.snapTime(ctx);
    const p = this.snapPrice(ctx);
    d.anchors = [{ time: t, price: p }];
    if (def.points >= 2) d.anchors.push({ time: t, price: p });
    this.drawings.push(d);
    this.creating = d;
    this.creatingDef = def;
    this.creatingForced = forced;
    this.select(d);
    this.chart.markDirty();
    if (def.points === 1) this.finalizeCreate();
    return true;
  }

  private advanceCreate(ctx: PointerCtx): boolean {
    const def = this.creatingDef!;
    if (def.points >= 2) {
      this.creating!.anchors[1] = { time: this.snapTime(ctx), price: this.snapPrice(ctx) };
    }
    this.finalizeCreate();
    return true;
  }

  private finalizeCreate() {
    const def = this.creatingDef!;
    const d = this.creating!;
    if (def.points >= 2) {
      const a = d.anchors[0];
      const b = d.anchors[1];
      if (a.time === b.time && a.price === b.price) {
        b.time = this.chart.timeOfX(this.chart.xOfTime(b.time) + 90);
      }
      if (def.initExtra) def.initExtra(d, this.chart);
    }
    const wasForced = this.creatingForced;
    this.creating = null;
    this.creatingDef = null;
    this.creatingForced = false;
    if (wasForced) {
      // shift-measure: keep it out of the persistent list; it's cleared on next click.
      // Clear the selection BEFORE the toolbar/popover callback so no popover shows.
      const i = this.drawings.indexOf(d);
      if (i >= 0) this.drawings.splice(i, 1);
      this.tempDrawing = d;
      this.selected = null;
      this.activeTool = "cursor";
      if (this.onActiveToolChange) this.onActiveToolChange();
      this.notify();
      this.chart.markDirty();
      return;
    }
    this.activeTool = "cursor";
    if (this.onActiveToolChange) this.onActiveToolChange();
    this.commit();
    this.notify();
  }

  private clearTemp() {
    this.tempDrawing = null;
    this.chart.markDirty();
  }

  onMove(ctx: PointerCtx): boolean {
    const { px, py } = ctx;
    if (this.creating && this.creatingDef) {
      const def = this.creatingDef;
      const t = this.snapTime(ctx);
      const p = this.snapPrice(ctx);
      if (def.points >= 2) {
        this.creating.anchors[1] = { time: t, price: p };
      } else {
        this.creating.anchors[0] = { time: t, price: p };
      }
      this.chart.markDirty();
      return true;
    }
    if (this.handleDrag && this.selected) {
      let hx = px,
        hy = py;
      this.selected.moveHandle(this.handleDrag.kind, hx, hy, this.chart);
      if (this.magnet) {
        // re-snap price of the moved anchor if it maps to one
      }
      this.chart.markDirty();
      this.chart.markOverlay();
      this.notify();
      return true;
    }
    if (this.bodyDrag && this.selected) {
      this.selected.translate(px - this.lastPx, py - this.lastPy, this.chart);
      this.lastPx = px;
      this.lastPy = py;
      this.chart.markDirty();
      this.chart.markOverlay();
      this.notify();
      return true;
    }
    // hover feedback
    this.hovered = this.activeTool === "cursor" ? this.hitTopmost(px, py) : null;
    return false;
  }

  onUp(_ctx: PointerCtx): boolean {
    // Two-click model: pointer-up does NOT finalize an in-progress drawing.
    if (this.creating) return true;
    if (this.handleDrag) {
      this.handleDrag = null;
      this.commit();
      this.notify();
      return true;
    }
    if (this.bodyDrag) {
      this.bodyDrag = false;
      this.commit();
      this.notify();
      return true;
    }
    return false;
  }

  onDblClick(ctx: PointerCtx): boolean {
    const hit = this.hitTopmost(ctx.px, ctx.py);
    if (hit) {
      this.select(hit);
      if (hit.type === "text") this.editSelectedText();
      this.notify();
      return true;
    }
    return false;
  }

  cursorFor(ctx: PointerCtx): string | null {
    if (this.activeTool !== "cursor") return "crosshair";
    if (this.selected) {
      if (this.hitHandle(this.selected, ctx.px, ctx.py) != null) return "pointer";
    }
    if (this.hitTopmost(ctx.px, ctx.py)) return "move";
    return null;
  }

  isBusy(): boolean {
    return this.creating !== null || this.handleDrag !== null || this.bodyDrag;
  }

  // ---------- rendering ----------
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, chart.plotWidth, chart.plotHeight);
    ctx.clip();
    for (const d of this.drawings) {
      d.render(ctx, chart, d === this.selected);
    }
    if (this.tempDrawing) this.tempDrawing.render(ctx, chart, false);
    ctx.restore();
  }

  renderOverlay(ctx: CanvasRenderingContext2D, chart: Chart) {
    if (!this.selected) return;
    const handles = this.selected.handles(chart);
    ctx.save();
    for (const h of handles) {
      ctx.beginPath();
      ctx.arc(h.x, h.y, 4.5, 0, Math.PI * 2);
      ctx.fillStyle = "#131722";
      ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = "#2962ff";
      ctx.stroke();
    }
    ctx.restore();
  }
}
